/**
 * @file Admin dashboard UI — pages/Dashboard/Admin/ToolCitation/AdminToolCitation.tsx.
 */

import React, { useEffect, useState } from "react";
import Button from "../../../../components/common/Button";
import FormFieldLabel from "../../../../components/common/FormFieldLabel";
import RichTextEditor from "../../../../components/common/RichTextEditor";
import TextInput from "../../../../components/common/TextInput";
import {
  DEFAULT_TOOL_CITATION,
  toolCitationService,
} from "../../../../services/toolCitationService";
import "../Admin.css";
import "./AdminToolCitation.css";

const AdminToolCitation: React.FC = () => {
  const [menuTitle, setMenuTitle] = useState(DEFAULT_TOOL_CITATION.menuTitle);
  const [description, setDescription] = useState(DEFAULT_TOOL_CITATION.description);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    const fetchConfig = async () => {
      setLoading(true);
      setError(null);
      setSuccessMessage(null);
      try {
        const response = await toolCitationService.get();
        if (response.success && response.data) {
          setMenuTitle(response.data.menuTitle);
          setDescription(response.data.description);
        } else {
          setMenuTitle(DEFAULT_TOOL_CITATION.menuTitle);
          setDescription(DEFAULT_TOOL_CITATION.description);
          setError(
            response.message ||
              "Could not load saved settings. Showing defaults — you can still edit and save."
          );
        }
      } catch (err: unknown) {
        setMenuTitle(DEFAULT_TOOL_CITATION.menuTitle);
        setDescription(DEFAULT_TOOL_CITATION.description);
        const message =
          (err as { response?: { data?: { message?: string } } })?.response?.data
            ?.message ||
          "Could not load saved settings. Showing defaults — restart the backend if this persists.";
        setError(message);
      } finally {
        setLoading(false);
      }
    };

    void fetchConfig();
  }, []);

  const handleSave = async (event?: React.FormEvent) => {
    event?.preventDefault();

    const trimmedTitle = menuTitle.trim();
    const trimmedDescription = description.trim();

    if (!trimmedTitle || !trimmedDescription) {
      setError("Menu title and description are required");
      return;
    }

    setSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const response = await toolCitationService.update({
        menuTitle: trimmedTitle,
        description: trimmedDescription,
      });

      if (response.success && response.data) {
        setMenuTitle(response.data.menuTitle);
        setDescription(response.data.description);
        setSuccessMessage("Tool citation saved successfully.");
      } else {
        setError(response.message || "Failed to save tool citation settings");
      }
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || "Failed to save tool citation settings";
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="background-form-container analysis-shell">
      <div className="analysis-banner">
        <span className="analysis-banner__title">Cite This Tool</span>
      </div>

      <div className="admin-form-root">
        <div className="admin-tool-citation dataset-container">
          {loading ? (
            <div className="dataset-section">
              <p className="admin-tool-citation__loading">Loading…</p>
            </div>
          ) : (
            <form
              className="admin-tool-citation__form"
              onSubmit={(event) => void handleSave(event)}
            >
              {error ? (
                <div className="admin-tool-citation__alert admin-tool-citation__alert--error">
                  {error}
                </div>
              ) : null}
              {successMessage ? (
                <div className="admin-tool-citation__alert admin-tool-citation__alert--success">
                  {successMessage}
                </div>
              ) : null}

              <div className="dataset-section">
                <h3 className="dataset-section-title">Citation settings</h3>

                <div className="admin-tool-citation__fields">
                  <div className="form-field">
                    <TextInput
                      label="Menu title"
                      name="menuTitle"
                      value={menuTitle}
                      onChange={(event) => setMenuTitle(event.target.value)}
                      placeholder="Cite this tool as"
                      disabled={saving}
                      required
                    />
                  </div>

                  <div className="form-field admin-tool-citation__description-field">
                    <FormFieldLabel
                      htmlFor="tool-citation-description"
                      label="Description"
                      required
                    />
                    <div id="tool-citation-description" className="admin-tool-citation__editor">
                      <RichTextEditor
                        value={description}
                        onChange={setDescription}
                        disabled={saving}
                        placeholder="Enter the citation text…"
                      />
                    </div>
                  </div>
                </div>

                <div className="admin-tool-citation__actions">
                  <Button
                    label={saving ? "Saving…" : "Save"}
                    type="submit"
                    disabled={saving}
                  />
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

/** React component — Admin Tool Citation. */
export default AdminToolCitation;
